<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'

/**Параметры */
const props = defineProps({
  /**Курсор наведен на видеоплеер */
  isOver: { type: Boolean, default: false },
  /**Текущее время просмотра */
  currentTime: { type: Number, default: 0 },
  /**Время видео */
  duration: { type: Number, default: 0 },
  /**Проигрывается ли видео */
  play: { type: Boolean, default: false },
})
/**События */
const emit = defineEmits<{
  /**Зажат лкм на слайдере */
  mouseDown: []
  /**Отпущен лкм на слайдере */
  mouseUp: []
  /**Смена времени проигрывания */
  switch: [time: number]
}>()

/**Зажата ли ЛКМ */
const isMouseDown = ref(false)
/**Была ли зажата ЛКМ хотя бы раз */
const isMouseDownOnce = ref(false)
/**DOM элемент полосы прокрутки */
const $slider = ref<HTMLElement>()
/**Ширина полосы */
const elWidth = ref()
/**Текущее время проигрывания */
const newCurrent = ref<number>()

/**Процент прошедшего времени */
const currentTimePercent = computed(() => {
  const currentTime = props.currentTime || 0
  const duration = props.duration || 0
  return (currentTime / duration || 0) * 100 + '%'
})
/**Стиль полоски с текущим временем */
const currentTimeStyle = computed(() => ({
  width: currentTimePercent.value,
}))
/**Стиль поинтера */
const pointerStyle = computed(() => ({
  left: `calc(${currentTimePercent.value} - 0.5rem)`,
}))
/**Получить время в формате 01:01:01 */
const currentTimeText = computed(() => {
  const hours = upScaleLength(Math.floor(props.currentTime / 3600))
  const minutes = upScaleLength(Math.floor(props.currentTime % 3600) / 60)
  const seconds = upScaleLength(Math.floor(props.currentTime % 3600) % 60)
  return `${hours}:${minutes}:${seconds}`
})

/**Добавление "0" для таймера */
const upScaleLength = (_seconds: number) => {
  const seconds = _seconds.toFixed(0).toString()
  return seconds.length < 2 ? '0' + seconds : seconds
}

/**После рендера компоенента */
onMounted(() => {
  window.addEventListener('resize', windowOnResize)
  windowOnResize()
})
/**При удалении компонента */
onUnmounted(() => {
  window.removeEventListener('resize', windowOnResize)
})

/**При изменении размера окна браузера */
function windowOnResize() {
  if (!$slider.value) return
  elWidth.value = $slider.value.getBoundingClientRect().width
}
/**При зажатии ЛКМ на полосе */
const onMouseDown = (e: MouseEvent) => {
  if (!isMouseDownOnce.value) emit('mouseDown')
  isMouseDownOnce.value = true
  isMouseDown.value = true
  if (!$slider.value) return
  windowOnResize()
  let sliderRect = $slider.value.getBoundingClientRect()
  let clickAt = ((e.pageX - sliderRect.left) / elWidth.value) * 100
  let secAtPercent = props.duration / 100
  newCurrent.value = clickAt * secAtPercent
  if (newCurrent.value >= props.duration) newCurrent.value = props.duration
  if (newCurrent.value <= 0) newCurrent.value = 0
  emit('switch', newCurrent.value)
  document.addEventListener('mouseup', documentOnMouseUp)
  document.addEventListener('mousemove', documentOnMouseMove)
}
/**При прекращении зажатия ЛКМ */
function documentOnMouseUp() {
  emit('mouseUp')
  document.removeEventListener('mousemove', documentOnMouseMove)
  document.removeEventListener('mouseup', documentOnMouseUp)
  isMouseDown.value = false
  isMouseDownOnce.value = false
}
/**При перемещении курсора по странице */
function documentOnMouseMove(e: MouseEvent) {
  if (isMouseDown.value) onMouseDown(e)
}
</script>
<template>
  <div
    class="tir-pm-video-player-slider"
    ref="$slider"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp"
  >
    <div class="tir-pm-video-player-slider_current" :style="currentTimeStyle" />
    <Transition name="tir-pm-video-player-slider_animation">
      <div
        class="tir-pm-video-player-slider_pointer"
        v-if="isOver"
        :style="pointerStyle"
      >
        <div v-if="isMouseDown" class="tir-pm-video-player-slider_pointer_time">
          {{ currentTimeText }}
          <div class="tir-pm-video-player-slider_pointer_time_triangle" />
        </div>
      </div>
    </Transition>
  </div>
</template>
<style lang="scss" scoped>
.tir-pm-video-player-slider {
  position: relative;
  height: 0.125rem;
  width: 100%;
  background-color: var(--bg-base);
  z-index: 2;
  cursor: pointer;
  user-select: none;
  &_current {
    position: absolute;
    left: 0;
    top: 0;
    height: 0.125rem;
    background-color: var(--accent);
  }
  &_pointer {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 1rem;
    height: 1rem;
    background-color: var(--accent-hover, #244178);
    border-radius: 50%;
    cursor: pointer;
    &_time {
      position: absolute;
      bottom: calc(100% + 0.75rem);
      left: 50%;
      transform: translateX(-50%);
      height: 3rem;
      padding: 0 1rem;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: var(--bg-base-inverted);
      color: var(--text-accent-inverted);
      border-radius: 0.5rem;
      &_triangle {
        position: absolute;
        max-height: 0;
        max-width: 0;
        border-style: solid;
        border-color: transparent;
        z-index: 10000;
        border-width: 0.625rem;
        border-top-width: 0;
        border-bottom-color: var(--bg-base-inverted);
        transform: rotate(-180deg);
        bottom: -0.5625rem;
      }
    }
  }
  &_animation-enter-active,
  &_animation-leave-active {
    height: 1rem;
    opacity: 1;
    transition: height 0.3s, opacity 0.3s;
  }
  &_animation-enter-from,
  &_animation-leave-to {
    height: 0.125rem;
    opacity: 0;
    transition: height 0.3s, opacity 0.3s;
  }
}
</style>
